import { LoginForm } from "@/components/features";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Form,
  ModeToggle,
  Typography,
} from "@/components/ui";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/shadcn/InputOTP/InputOTP";
import {
  getUserSessionQueryOptions,
  useConfirmSignIn,
  useSignIn,
} from "@/lib/data/react-query/auth/auth";
import { redirectIfAuthenticated } from "@/lib/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import { LawWorksLogo } from "@/components/ui/LawWorksLogo/LawWorksLogo";
import { queryKeys } from "@/lib/data/static/query-keys";
import { signOut } from "@aws-amplify/auth";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { z } from "zod";

const fallback = "/";
const SIGN_IN_CHALLENGE_TIMEOUT_MS = 165000;

export const loginFormSchema = z.object({
  email: z.string().max(50),
  password: z.string(),
});

export const Route = createFileRoute("/login")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  component: RouteComponent,
  beforeLoad: ({ context, location }) => {
    redirectIfAuthenticated({ session: context.session, location });
  },
});

function RouteComponent() {
  const search = useSearch({ from: "/login" });
  const signIn = useSignIn();
  const confirmSignIn = useConfirmSignIn();
  const navigate = useNavigate({ from: "/login" });
  const queryClient = useQueryClient();
  const { data: session } = useQuery(getUserSessionQueryOptions());

  // MFA state management for inline handling
  const [mfaStep, setMfaStep] = useState<string | null>(null);
  const [qrCodeUri, setQrCodeUri] = useState<string>("");
  const [sharedSecret, setSharedSecret] = useState<string>("");
  const [challengeStartedAt, setChallengeStartedAt] = useState<number | null>(
    null,
  );
  const [isRedirecting, setIsRedirecting] = useState(false);

  const form = useForm<z.infer<typeof loginFormSchema>>({
    resolver: zodResolver(loginFormSchema),
    values: {
      email: "",
      password: "",
    },
    mode: "onSubmit",
  });

  // MFA code form for verification with 6-digit validation
  const mfaForm = useForm<{ code: string }>({
    defaultValues: { code: "" },
    mode: "onChange",
  });

  const isExpiredSignInSessionError = (error: unknown) => {
    if (!(error instanceof Error)) {
      return false;
    }

    return (
      error.name === "NotAuthorizedException" &&
      /session is expired/i.test(error.message)
    );
  };

  const clearAmplifyAuthState = useCallback(async () => {
    try {
      await signOut();
    } catch {
      // Ignore local sign-out failures when clearing a stale challenge.
    }
  }, []);

  const resetPendingSignInState = useCallback(
    async (message?: string) => {
      await clearAmplifyAuthState();

      setChallengeStartedAt(null);
      setMfaStep(null);
      setQrCodeUri("");
      setSharedSecret("");
      mfaForm.reset({ code: "" });
      mfaForm.clearErrors();

      localStorage.removeItem("mfa-setup-required");
      localStorage.removeItem("totp-setup-uri");
      localStorage.removeItem("totp-shared-secret");
      localStorage.removeItem("user-email");

      form.setValue("password", "", { shouldDirty: true });

      if (message) {
        form.setError("password", {
          type: "manual",
          message,
        });
        return;
      }

      form.clearErrors("password");
    },
    [clearAmplifyAuthState, form, mfaForm],
  );

  const onSubmit = async () => {
    const email = form.getValues("email");

    // Store email for TOTP setup personalization
    localStorage.setItem("user-email", email);

    try {
      const res = await signIn.mutateAsync({
        username: email,
        password: form.getValues("password"),
      });

      switch (res.signInStep) {
        case "DONE":
          setChallengeStartedAt(null);
          // Clean up email after successful login
          localStorage.removeItem("user-email");
          await queryClient.invalidateQueries({
            queryKey: [queryKeys.currentUser],
          });
          break;
        case "RESET_PASSWORD":
          await navigate({ to: "/change-password" });
          break;
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
          await navigate({ to: "/change-password" });
          break;
        // Handle MFA challenges inline
        case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
          setChallengeStartedAt(Date.now());
          setMfaStep("CONTINUE_SIGN_IN_WITH_TOTP_SETUP");
          // In REQUIRED MFA, TOTP details are provided in the sign-in response
          // Use the stored email for personalization
          const storedEmail = localStorage.getItem("user-email") || "User";
          const uri = res.totpSetupDetails.getSetupUri("LawWorks", storedEmail);
          setQrCodeUri(uri.toString());
          setSharedSecret(res.totpSetupDetails.sharedSecret);
          break;
        }
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
          setChallengeStartedAt(Date.now());
          setMfaStep(res.signInStep);
          break;
        case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
          setChallengeStartedAt(Date.now());
          setMfaStep("CONTINUE_SIGN_IN_WITH_MFA_SELECTION");
          break;
        default: {
          toast.error(
            `You have an unhandled auth step (${res.signInStep}), please contact your administrator`,
          );
          break;
        }
      }
    } catch (error) {
      if (isExpiredSignInSessionError(error)) {
        await resetPendingSignInState(
          "Your previous sign-in session expired. Please sign in again.",
        );
        return;
      }

      form.setError("password", {
        type: "manual",
        message: "Invalid email or password",
      });
    }
  };

  // TOTP setup details come from sign-in response in REQUIRED MFA mode

  const handleMFAVerification = async () => {
    const code = mfaForm.getValues("code");

    // Ensure we have a 6-digit code
    if (code.length !== 6) {
      mfaForm.setError("code", {
        message: "Please enter a complete 6-digit code",
      });
      return;
    }

    try {
      // All MFA verification uses confirmSignIn in REQUIRED MFA mode
      const result = await confirmSignIn.mutateAsync({
        challengeResponse: code,
      });

      if (result.signInStep === "DONE") {
        // Match original login flow - invalidate queries and let useEffect handle navigation
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.session],
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.currentUser],
        });

        // Clean up stored email after successful MFA verification
        localStorage.removeItem("user-email");

        // Clear MFA state and let session-based redirect handle navigation
        setChallengeStartedAt(null);
        setMfaStep(null);
      } else {
        // Handle any additional MFA steps if needed
        toast.error("Additional verification required");
      }
    } catch (error) {
      console.error("MFA verification error:", error);

      if (isExpiredSignInSessionError(error)) {
        await resetPendingSignInState(
          "Your verification session expired. Please sign in again.",
        );
        return;
      }

      mfaForm.setError("code", {
        message: "Invalid code. Please try again.",
      });
    }
  };

  const handleMFASubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleMFAVerification();
  };

  // MFA success/cancel handled by individual MFA route components

  useEffect(() => {
    // Check for MFA setup required from password change
    const mfaRequired = localStorage.getItem("mfa-setup-required");
    if (mfaRequired) {
      setChallengeStartedAt(Date.now());
      setMfaStep(mfaRequired);

      if (mfaRequired === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
        const uri = localStorage.getItem("totp-setup-uri");
        const secret = localStorage.getItem("totp-shared-secret");
        if (uri && secret) {
          setQrCodeUri(uri);
          setSharedSecret(secret);
        }
      }

      // Clear the localStorage items
      localStorage.removeItem("mfa-setup-required");
      localStorage.removeItem("totp-setup-uri");
      localStorage.removeItem("totp-shared-secret");
      return;
    }

    if (session?.tokens?.idToken?.toString() && !isRedirecting) {
      void (async () => {
        setIsRedirecting(true);
        try {
          const redirect = search.redirect;
          if (redirect) {
            await navigate({ to: redirect });
          } else {
            await navigate({ to: fallback });
          }
        } finally {
          setIsRedirecting(false);
        }
      })();
    }
  }, [session?.tokens?.idToken, navigate, search.redirect, isRedirecting]);

  useEffect(() => {
    if (!challengeStartedAt || !mfaStep) {
      return;
    }

    const remainingMs =
      SIGN_IN_CHALLENGE_TIMEOUT_MS - (Date.now() - challengeStartedAt);

    if (remainingMs <= 0) {
      void resetPendingSignInState(
        "Your sign-in session timed out. Please sign in again.",
      );
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void resetPendingSignInState(
        "Your sign-in session timed out. Please sign in again.",
      );
    }, remainingMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [challengeStartedAt, mfaStep, resetPendingSignInState]);

  // Show MFA setup/verification if in MFA flow
  if (mfaStep) {
    return (
      <div className="flex h-[99vh] w-[99vw] grow content-center justify-center overflow-hidden">
        <div className="flex w-full max-w-md flex-col items-center justify-center space-y-6 p-8">
          <LawWorksLogo className="h-12" />

          {mfaStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" ? (
            <Card className="w-full">
              <CardHeader>
                <h3 className="text-center text-xl font-semibold">
                  Set up authenticator
                </h3>
                <p className="text-muted-foreground text-center">
                  Scan QR code with your authenticator app
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {qrCodeUri && (
                  <div className="flex flex-col items-center space-y-4">
                    <div className="rounded-lg bg-white p-4">
                      <QRCode value={qrCodeUri} size={180} />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigator.clipboard.writeText(sharedSecret)
                      }
                      size="sm"
                    >
                      Copy secret key
                    </Button>
                    {sharedSecret && (
                      <div className="rounded bg-gray-100 p-2 font-mono text-xs">
                        {sharedSecret}
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleMFASubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Enter verification code
                    </label>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={mfaForm.watch("code")}
                        onChange={(value) => {
                          mfaForm.setValue("code", value);
                        }}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <div className="flex items-center">
                          <div className="h-0.5 w-2 bg-gray-300" />
                        </div>
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    {mfaForm.formState.errors.code && (
                      <p className="text-center text-sm text-red-600">
                        {mfaForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={mfaForm.watch("code").length !== 6}
                  >
                    Complete setup
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      void resetPendingSignInState();
                    }}
                  >
                    Back to sign in
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card className="w-full">
              <CardHeader>
                <h3 className="text-center text-xl font-semibold">
                  Enter verification code
                </h3>
                <p className="text-muted-foreground text-center">
                  Enter the code from your authenticator app
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleMFASubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Verification code
                    </label>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={mfaForm.watch("code")}
                        onChange={(value) => {
                          mfaForm.setValue("code", value);
                        }}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <div className="flex items-center">
                          <div className="h-0.5 w-2 bg-gray-300" />
                        </div>
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    {mfaForm.formState.errors.code && (
                      <p className="text-center text-sm text-red-600">
                        {mfaForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={mfaForm.watch("code").length !== 6}
                  >
                    Verify
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      void resetPendingSignInState();
                    }}
                  >
                    Back to sign in
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // MFA flow handled by separate routes (mfa-setup, mfa-verify, mfa-select)

  return (
    <div className="flex h-[99vh] w-[99vw] grow content-center justify-center overflow-hidden">
      <div className="sm:px-auto my-auto w-full max-w-[500px] px-4">
        <Card className="p-6">
          <CardHeader>
            <LawWorksLogo className="justify-self-center" />
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <LoginForm
                onSubmit={onSubmit}
                isLoading={signIn.isPending || isRedirecting}
              />
            </Form>
          </CardContent>
          <div className="float-right pr-6">
            <ModeToggle />
          </div>
          <CardFooter className="flex flex-col items-start">
            {/* <span className="flex flex-wrap mt-2 text-sm font-light">
              <Typography className="pr-1">{`Don't have an account?`}</Typography>
              <Link to="/register">
                <Typography variant="link">Create a firm</Typography>
              </Link>
            </span> */}
            <span className="mt-2 flex flex-wrap text-sm font-light">
              <Typography className="pr-1">Forgot password?</Typography>
              <Link to="/forgot-password">
                <Typography variant="link">Reset it</Typography>
              </Link>
            </span>
          </CardFooter>
        </Card>
      </div>
      <div></div>
    </div>
  );
}
